import React, { useState, useEffect } from 'react';
import { getItems, getCategories, getOpenTabs, openTab, addItemToTab, closeTab, searchMembers, getMember, removeTabItem, voidTab, getCigars, API_BASE_URL } from '../utils/api';
import { useResponsive } from '../hooks/useResponsive';
import BottomNav from '../components/BottomNav';
import BottomSheet, { CheckoutFAB } from '../components/BottomSheet';

const POS = ({ staff, onLogout, onAdmin, onHistory, onMembers, onReceiving, onTaxReport, onMarketing, onCigars }) => {
  const [items, setItems] = useState([]);
  const [cigars, setCigars] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openTabs, setOpenTabs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [tabItems, setTabItems] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [newTabName, setNewTabName] = useState('');
  const [showNewTab, setShowNewTab] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const [tip, setTip] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [creditApplied, setCreditApplied] = useState('');
  const [secondaryMethod, setSecondaryMethod] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cigarSearch, setCigarSearch] = useState('');

  const { isMobile } = useResponsive();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [itemsRes, catsRes, tabsRes, cigarsRes] = await Promise.all([
        getItems(),
        getCategories(),
        getOpenTabs(),
        getCigars({ limit: 1000 }),
      ]);
      setItems(Array.isArray(itemsRes) ? itemsRes : itemsRes.data || []);
      setCategories(Array.isArray(catsRes) ? catsRes : catsRes.data || []);
      setOpenTabs(Array.isArray(tabsRes) ? tabsRes : tabsRes.data || []);
      setCigars((cigarsRes && cigarsRes.cigars) || (Array.isArray(cigarsRes) ? cigarsRes : []));
      if (Array.isArray(catsRes) && catsRes.length > 0) setSelectedCategory(catsRes[0].id);
    } catch (err) { notify('Failed to load data', 'error'); }
  };

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSearchMember = async (q) => {
    setMemberSearch(q);
    if (q.length > 1) {
      try {
        const res = await searchMembers(q);
        setMemberResults(Array.isArray(res) ? res : res.data || []);
      } catch (err) {}
    } else { setMemberResults([]); }
  };

  const handleOpenTab = async () => {
    if (!newTabName) return;
    try {
      const res = await openTab({ tab_name: newTabName, staff_id: staff.id, member_id: selectedMember ? selectedMember.id : null });
      setOpenTabs([...openTabs, Array.isArray(res) ? res : res.data || res]);
      setSelectedTab(Array.isArray(res) ? res[0] : res.data || res);
      setTabItems([]);
      setNewTabName('');
      setShowNewTab(false);
      notify('Tab opened');
    } catch (err) { notify('Failed to open tab', 'error'); }
  };

  const handleSelectTab = async (tab) => {
    setSelectedTab(tab);
    setTabItems([]);
    if (tab.member_id) {
      try {
        const res = await getMember(tab.member_id);
        setSelectedMember(Array.isArray(res) ? res[0] : res.data || res);
      } catch(e) {}
    } else { setSelectedMember(null); }
  };

  const handleAddItem = async (item) => {
    if (!selectedTab) { notify('Open a tab first', 'error'); return; }
    try {
      const row = await addItemToTab(selectedTab.id, { item_id: item.id, quantity: 1, unit_price: item.price });
      const tabItemId = row && (row.id || (row.data && row.data.id));
      setTabItems(prev => [...prev, {
        tab_item_id: tabItemId,
        item_id: item.id,
        item_name: item.name,
        quantity: 1,
        unit_price: parseFloat(item.price),
        category_id: item.category_id,
      }]);
      notify(item.name + ' added');
    } catch (err) { notify('Failed to add item', 'error'); }
  };

  const handleRemoveItem = async (item) => {
    if (!selectedTab) return;
    try {
      await removeTabItem(selectedTab.id, item.tab_item_id);
      setTabItems(prev => prev.filter(i => i.tab_item_id !== item.tab_item_id));
      notify(item.item_name + ' removed');
    } catch (err) {
      notify('Failed to remove item', 'error');
    }
  };

  const handleVoidTab = async () => {
    if (!selectedTab) return;
    if (!window.confirm('Void this tab? This cannot be undone.')) return;
    try {
      await voidTab(selectedTab.id, 'Cancelled by staff', staff.id);
      setOpenTabs(openTabs.filter(t => t.id !== selectedTab.id));
      setSelectedTab(null);
      setTabItems([]);
      setSelectedMember(null);
      notify('Tab voided');
    } catch (err) {
      notify('Failed to void tab', 'error');
    }
  };

  const getSubtotal = () => tabItems.reduce((sum, i) => sum + (parseFloat(i.unit_price) * i.quantity), 0);
  const getTax = () => getSubtotal() * 0.06;
  const getTotal = () => getSubtotal() + getTax() + parseFloat(tip || 0);
  const creditNum = () => {
    const n = parseFloat(creditApplied);
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const handleCloseTab = async () => {
    if (!selectedTab) return;
    setLoading(true);
    try {
      const round2 = (n) => Math.round(n * 100) / 100;
      const total = round2(getTotal());
      const tax = round2(getTax());
      const tipAmount = round2(parseFloat(tip || 0));
      const isCredit = paymentMethod === 'member_credit';
      const usedCredit = isCredit ? round2(Math.min(creditNum(), total)) : 0;
      const remainder = round2(total - usedCredit);
      const remainderMethod = isCredit ? secondaryMethod : paymentMethod;
      const finalMethod = isCredit
        ? (remainder > 0 ? secondaryMethod : 'member_credit')
        : paymentMethod;

      const payments = [];
      if (usedCredit > 0) {
        payments.push({ method: 'member_credit', amount: usedCredit });
      }
      if (remainder > 0 && remainderMethod === 'card') {
        payments.push({
          method: 'card',
          amount: remainder,
          stripe_payment_intent_id: 'sim_' + Date.now(),
        });
      } else if (remainder > 0 && remainderMethod === 'cash') {
        payments.push({
          method: 'cash',
          amount: remainder,
          cash_tendered: remainder,
          cash_change: 0,
        });
      }

      await closeTab(selectedTab.id, { payments, tax, tip: tipAmount });
      const paymentSummary = {
        tabName: selectedTab.tab_name,
        subtotal: getSubtotal(), tax, tip: tipAmount,
        total, creditUsed: usedCredit,
        charged: Math.max(0, total - usedCredit),
        method: finalMethod,
        member: selectedMember ? selectedMember.name : null,
        remainingCredit: selectedMember ? parseFloat(selectedMember.credit_balance) - usedCredit : 0,
      };
      setOpenTabs(openTabs.filter(t => t.id !== selectedTab.id));
      setSelectedTab(null); setTabItems([]); setSelectedMember(null);
      setShowPayment(false); setCartOpen(false); setTip(0);
      setPaymentMethod(null); setCreditApplied(''); setSecondaryMethod(null);
      setLastPayment(paymentSummary); setShowConfirmation(true);
    } catch (err) { notify(err.message || 'Failed to close tab', 'error'); }
    finally { setLoading(false); }
  };

  const filteredItems = (() => {
    let base = selectedCategory ? items.filter(i => i.category_id === selectedCategory) : items;
    if (selectedCategory === 1) base = base.filter(i => i.name.indexOf('Box of') === -1);
    return base;
  })();

  const cigarMap = {};
  cigars.forEach(c => { if (c && c.id != null) cigarMap[c.id] = c; });

  const tipOptions = [0, 18, 20, 25];

  // ── Cart panel content (shared between desktop rightPanel + mobile BottomSheet) ──
  const CartContent = () => (
    <>
      {/* Tabs row */}
      <div style={styles.tabsRow}>
        <button style={styles.newTabBtn} onPointerDown={(e) => { e.preventDefault(); setCartOpen(false); setShowNewTab(true); }}>
          + New Tab
        </button>
        <div style={styles.tabsList}>
          {openTabs.map(tab => (
            <button key={tab.id}
              style={{ ...styles.tabChip, background: selectedTab && selectedTab.id === tab.id ? '#C9A84C' : '#2A2A2A', color: selectedTab && selectedTab.id === tab.id ? '#000' : '#CCC' }}
              onPointerDown={(e) => { e.preventDefault(); handleSelectTab(tab); }}>
              {tab.tab_name || tab.member_name || 'Tab ' + tab.id}
            </button>
          ))}
        </div>
      </div>

      {/* Tab items */}
      <div style={{ ...styles.tabItems, flex: 1 }}>
        {selectedTab ? (
          <>
            <div style={styles.tabHeader}>
              <span style={styles.tabName}>{selectedTab.tab_name || 'Tab ' + selectedTab.id}</span>
              <button
                onClick={handleVoidTab}
                style={{ background: '#E53935', border: 'none', borderRadius: 4, color: '#fff', fontSize: 11, fontWeight: 600, padding: '6px 12px', cursor: 'pointer', marginLeft: 8, minHeight: 32 }}
              >
                Void
              </button>
              {selectedMember && <span style={styles.memberBadge}>👤 {selectedMember.name} · ${parseFloat(selectedMember.credit_balance || 0).toFixed(2)} credit</span>}
            </div>
            <div style={styles.itemsList}>
              {tabItems.map((item, i) => (
                <div key={i} style={styles.tabItem}>
                  <span style={styles.tabItemName}>{item.item_name}</span>
                  <span style={styles.tabItemQty}>x{item.quantity}</span>
                  <span style={styles.tabItemPrice}>${(parseFloat(item.unit_price) * item.quantity).toFixed(2)}</span>
                  <button onClick={() => handleRemoveItem(item)} style={{ background: 'none', border: 'none', color: '#666', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
                </div>
              ))}
              {tabItems.length === 0 && <p style={styles.emptyTab}>Add items from the menu</p>}
            </div>
          </>
        ) : (
          <div style={styles.noTab}><p>Select or open a tab to begin</p></div>
        )}
      </div>

      {/* Totals */}
      {selectedTab && (
        <div style={styles.totals}>
          {selectedMember && parseFloat(selectedMember.credit_balance) > 0 && (
            <div style={styles.creditRow}>
              <span>Available Credit</span>
              <span style={{ color: '#5DBB63' }}>${parseFloat(selectedMember.credit_balance).toFixed(2)}</span>
            </div>
          )}
          <div style={styles.totalRow}><span>Subtotal</span><span>${getSubtotal().toFixed(2)}</span></div>
          <div style={styles.totalRow}><span>Tax (6%)</span><span>${getTax().toFixed(2)}</span></div>
          {/* Tip hidden */}
          <div style={{ ...styles.totalRow, ...styles.totalFinal }}><span>TOTAL</span><span>${getTotal().toFixed(2)}</span></div>
          <button style={styles.chargeBtn}
            onPointerDown={(e) => { e.preventDefault(); setCartOpen(false); setShowPayment(true); }}
            disabled={tabItems.length === 0}>
            Charge ${getTotal().toFixed(2)}
          </button>
        </div>
      )}
    </>
  );

  return (
    <div style={styles.container}>
      {notification && (
        <div style={{ ...styles.notification, background: notification.type === 'error' ? '#C0392B' : '#27AE60' }}>
          {notification.message}
        </div>
      )}

      {/* ── HEADER ────────────────────────────────────────────────────────── */}
      {isMobile ? (
        // Mobile header: compact — wordmark + staff name only, nav via BottomNav
        <div style={styles.headerMobile}>
          <img
            src={process.env.REACT_APP_LOUNGE_LOGO}
            alt="Lounge logo"
            style={{ height: 'var(--logo-height-mobile)', width: 'auto', flexShrink: 0, display: 'block' }}
          />
          <span style={{ color: '#666', fontSize: 12 }}>👤 {staff.name}</span>
          <button style={styles.headerBtnSm} onPointerDown={(e) => { e.preventDefault(); onLogout(); }}>
            Sign Out
          </button>
        </div>
      ) : (
        // Desktop header: full nav buttons
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <img
              src={process.env.REACT_APP_LOUNGE_LOGO}
              alt="Lounge logo"
              style={{ height: 'var(--logo-height-desktop)', width: 'auto', marginRight: 16, flexShrink: 0, display: 'block' }}
            />
            <span style={styles.headerStaff}>👤 {staff.name} · {staff.role}</span>
          </div>
          <div style={styles.headerRight}>
            <button style={styles.headerBtn} onClick={onHistory}>📋 History</button>
            <button style={styles.headerBtn} onClick={onMembers}>👥 Members</button>
            <button style={styles.headerBtn} onClick={onReceiving}>📦 Receiving</button>
            <button style={styles.headerBtn} onClick={onTaxReport}>📊 Revenue Intelligence</button>
            <button style={styles.headerBtn} onClick={onMarketing}>📣 Marketing</button>
            <button style={styles.headerBtn} onClick={onCigars}>Cigar Intelligence</button>
            <button style={styles.headerBtn} onClick={onAdmin}>⚙️ Admin</button>
            <button style={styles.headerBtn} onClick={onLogout}>Sign Out</button>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ──────────────────────────────────────────────────── */}
      <div style={{
        ...styles.main,
        // On mobile: full height minus header (56px) and bottom nav (56px + safe area)
        height: isMobile
          ? 'calc(100dvh - 56px - 56px - env(safe-area-inset-bottom))'
          : undefined,
        flex: isMobile ? 'none' : 1,
      }}>
        {/* Left panel — product grid, full width on mobile */}
        <div style={{ ...styles.leftPanel, borderRight: isMobile ? 'none' : '1px solid #2A2A2A' }}>
          {/* Category tabs */}
          <div style={styles.categories}>
            {categories.map(cat => (
              <button key={cat.id}
                style={{ ...styles.categoryBtn, background: selectedCategory === cat.id ? '#C9A84C' : '#2A2A2A', color: selectedCategory === cat.id ? '#000' : '#CCC' }}
                onPointerDown={(e) => { e.preventDefault(); setSelectedCategory(cat.id); if (cat.id === 1) setCigarSearch(''); }}>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Cigars: search-first list */}
          {selectedCategory === 1 ? (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div style={{ padding: 12, borderBottom: '1px solid #2A2A2A', background: '#151515' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    autoFocus
                    type="text"
                    value={cigarSearch}
                    onChange={(e) => setCigarSearch(e.target.value)}
                    placeholder="Search by name or scan UPC..."
                    style={{
                      width: '100%', padding: '12px 40px 12px 14px', fontSize: 15,
                      background: '#0D0D0D', border: '1px solid #3A3A3A', borderRadius: 8,
                      color: '#E8E0D0', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  {cigarSearch && (
                    <button
                      type="button"
                      onClick={() => setCigarSearch('')}
                      aria-label="Clear search"
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'transparent', border: 'none', borderRadius: 6,
                        color: '#C9A84C', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 0,
                      }}
                    >×</button>
                  )}
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {(() => {
                  const q = cigarSearch.trim().toLowerCase();
                  const list = cigars.filter(c => c.active === true).filter(c => {
                    if (!q) return true;
                    const combined = ((c.manufacturer || '') + ' ' + (c.brand_line || '') + ' ' + (c.vitola_name || '')).toLowerCase();
                    return combined.indexOf(q) !== -1;
                  }).sort((a, b) => {
                    const aHas = (parseInt(a.stock_singles, 10) || 0) > 0 || (parseInt(a.stock_boxes, 10) || 0) > 0;
                    const bHas = (parseInt(b.stock_singles, 10) || 0) > 0 || (parseInt(b.stock_boxes, 10) || 0) > 0;
                    if (aHas !== bHas) return aHas ? -1 : 1;
                    const mCmp = (a.manufacturer || '').localeCompare(b.manufacturer || '');
                    if (mCmp !== 0) return mCmp;
                    const blCmp = (a.brand_line || '').localeCompare(b.brand_line || '');
                    if (blCmp !== 0) return blCmp;
                    return (a.vitola_name || '').localeCompare(b.vitola_name || '');
                  });
                  if (list.length === 0) {
                    return <div style={{ padding: 32, textAlign: 'center', color: '#444' }}>No cigars match "{cigarSearch}"</div>;
                  }
                  return list.map(c => {
                    const singlesOut = !c.stock_singles || parseInt(c.stock_singles, 10) === 0;
                    const boxesOut = !c.stock_boxes || parseInt(c.stock_boxes, 10) === 0 || !c.box_price;
                    const stockColor = (!singlesOut || !boxesOut) ? '#5DBB63' : '#E53935';
                    return (
                      <div key={c.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 14px', borderBottom: '1px solid #222', background: '#151515',
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Georgia' }}>
                            {c.manufacturer}
                          </div>
                          <div style={{ fontSize: 14, color: '#E8E0D0', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.brand_line} — {c.vitola_name}
                          </div>
                        </div>
                        <div style={{ minWidth: 150, fontSize: 12, color: '#AAA', lineHeight: 1.5 }}>
                          <div>${parseFloat(c.single_price || 0).toFixed(2)} / stick</div>
                          {c.box_price && c.box_quantity && (
                            <div>${parseFloat(c.box_price).toFixed(2)} / box of {c.box_quantity}</div>
                          )}
                        </div>
                        <div style={{ minWidth: 120, fontSize: 11, color: stockColor }}>
                          {parseInt(c.stock_singles || 0, 10)} singles · {parseInt(c.stock_boxes || 0, 10)} boxes
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            disabled={false}
                            onPointerDown={(e) => {
                              e.preventDefault();
                              handleAddItem({ ...c, price: c.single_price, name: c.brand_line + ' — ' + c.vitola_name, category_id: 1 });
                            }}
                            style={{
                              padding: '8px 12px', background: '#C9A84C',
                              border: 'none', borderRadius: 6, color: '#000',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >+ Single</button>
                          <button
                            disabled={false}
                            onPointerDown={(e) => {
                              e.preventDefault();
                              if (!c.box_price) return;
                              handleAddItem({ ...c, price: c.box_price, name: c.brand_line + ' — ' + c.vitola_name + ' (Box of ' + c.box_quantity + ')', category_id: 1 });
                            }}
                            style={{
                              padding: '8px 12px', background: c.box_price ? '#1A1005' : '#2A2A2A',
                              border: `1px solid ${c.box_price ? '#5A3010' : '#3A3A3A'}`, borderRadius: 6,
                              color: c.box_price ? '#C9A84C' : '#666', fontSize: 12, fontWeight: 600,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >+ Box{c.box_quantity ? ' of ' + c.box_quantity : ''}</button>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
          <div style={{
            ...styles.itemsGrid,
            // 2 columns on mobile, auto-fill on desktop
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(140px, 1fr))',
            paddingBottom: isMobile ? 8 : 16,
          }}>
            {filteredItems.map(item => (
              <button key={item.id} style={styles.itemBtn}
                onPointerDown={(e) => { e.preventDefault(); handleAddItem(item); }}>
                {item.image_url ? (
                  <img
                    src={item.image_url.startsWith('/images') ? API_BASE_URL + item.image_url : item.image_url}
                    alt={item.name}
                    style={{ width: '100%', height: isMobile ? 70 : 80, objectFit: 'contain', borderRadius: 6, marginBottom: 6, background: '#111' }}
                    onError={e => e.target.style.display = 'none'}
                  />
                ) : (
                  <div style={{ width: '100%', height: isMobile ? 70 : 80, background: '#1A1A1A', borderRadius: 6, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#333', fontSize: 28 }}>{item.name.charAt(0)}</span>
                  </div>
                )}
                <span style={styles.itemName}>{item.name}</span>
                <span style={styles.itemPrice}>${parseFloat((cigarMap[item.id] && cigarMap[item.id].single_price) || item.price).toFixed(2)}</span>
                {item.category_id === 1 && cigarMap[item.id] && cigarMap[item.id].box_price && cigarMap[item.id].box_quantity && (
                  <div
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const c = cigarMap[item.id];
                      handleAddItem({ ...item, price: c.box_price, name: item.name + ' (Box of ' + c.box_quantity + ')' });
                    }}
                    style={{ marginTop: 8, padding: '4px 8px', background: '#1A1005', border: '1px solid #5A3010', borderRadius: 6, color: '#C9A84C', fontSize: 11, cursor: 'pointer', textAlign: 'center' }}>
                    Box of {cigarMap[item.id].box_quantity} — ${parseFloat(cigarMap[item.id].box_price).toFixed(2)}
                  </div>
                )}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div style={styles.emptyItems}><p style={{ color: '#444' }}>No items in this category yet</p></div>
            )}
          </div>
          )}
        </div>

        {/* Right panel — desktop only */}
        {!isMobile && (
          <div style={styles.rightPanel}>
            <CartContent />
          </div>
        )}
      </div>

      {/* ── MOBILE: CheckoutFAB + BottomSheet cart ────────────────────────── */}
      {isMobile && (
        <>
          <CheckoutFAB
            visible={true}
            itemCount={tabItems.length}
            total={getTotal()}
            onPress={() => setCartOpen(true)}
          />
          <BottomSheet
            isOpen={cartOpen}
            onClose={() => setCartOpen(false)}
            title={selectedTab ? selectedTab.tab_name : 'Cart'}
            snapToFull={false}
          >
            <CartContent />
          </BottomSheet>
        </>
      )}

      {/* ── MOBILE: Bottom navigation ─────────────────────────────────────── */}
      {isMobile && (
        <BottomNav
          activeTab="pos"
          onChange={(tab) => {
            if (tab === 'members')   onMembers();
            if (tab === 'inventory') onReceiving();
            if (tab === 'reports')   onTaxReport();
            if (tab === 'marketing') onMarketing();
            if (tab === 'settings')  onAdmin();
          }}
          openTabCount={openTabs.length}
        />
      )}

      {/* ── MODALS (shared desktop + mobile) ─────────────────────────────── */}

      {/* New Tab modal */}
      {showNewTab && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3 style={styles.modalTitle}>Open New Tab</h3>
            <input style={styles.input} placeholder="Tab name or guest name" value={newTabName} onChange={e => setNewTabName(e.target.value)} />
            <div style={{ position: 'relative' }}>
              <input style={{ ...styles.input, paddingRight: 40 }} placeholder="Search member (optional)" value={memberSearch} onChange={e => handleSearchMember(e.target.value)} />
              {memberSearch && (
                <button
                  type="button"
                  onClick={() => handleSearchMember('')}
                  aria-label="Clear search"
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none', borderRadius: 6,
                    color: '#C9A84C', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 0,
                  }}
                >×</button>
              )}
            </div>
            {memberResults.map(m => (
              <button key={m.id} style={styles.memberResult} onClick={() => { setSelectedMember(m); setNewTabName(m.name); setMemberResults([]); setMemberSearch(''); }}>
                {m.name} · {m.tier || 'Member'}
                <span style={styles.creditBadge}>${parseFloat(m.credit_balance || 0).toFixed(2)} credit</span>
              </button>
            ))}
            {selectedMember && <div style={styles.selectedMember}>✓ {selectedMember.name} · Credit: ${parseFloat(selectedMember.credit_balance || 0).toFixed(2)}</div>}
            <div style={styles.modalBtns}>
              <button style={styles.cancelBtn} onClick={() => { setShowNewTab(false); setSelectedMember(null); }}>Cancel</button>
              <button style={styles.confirmBtn} onClick={handleOpenTab}>Open Tab</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {showPayment && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3 style={styles.modalTitle}>Complete Payment</h3>
            <div style={styles.totalBig}>${getTotal().toFixed(2)}</div>
            {selectedMember && parseFloat(selectedMember.credit_balance) > 0 && (
              <div style={styles.creditAvailable}>
                💳 ${parseFloat(selectedMember.credit_balance).toFixed(2)} in member credit available
              </div>
            )}
            <p style={styles.label}>Payment Method</p>
            <div style={styles.payMethodGrid}>
              {[
                { key: 'card', label: '💳 Card' },
                { key: 'cash', label: '💵 Cash' },
              ].map(m => (
                <button key={m.key}
                  style={{ ...styles.payMethodBtn, ...(paymentMethod === m.key ? styles.payMethodBtnActive : {}) }}
                  onClick={() => { setPaymentMethod(m.key); setCreditApplied(''); setSecondaryMethod(null); }}>
                  {m.label}
                </button>
              ))}
              {(() => {
                const memberHasCredit = selectedMember && parseFloat(selectedMember.credit_balance) > 0;
                return (
                  <div style={styles.payMethodWrap}>
                    <button
                      style={{
                        ...styles.payMethodBtn,
                        ...(paymentMethod === 'member_credit' ? styles.payMethodBtnActive : {}),
                        ...(!memberHasCredit ? styles.payMethodBtnDisabled : {}),
                      }}
                      disabled={!memberHasCredit}
                      onClick={() => { setPaymentMethod('member_credit'); setCreditApplied(''); setSecondaryMethod(null); }}>
                      🏅 Member Credit
                    </button>
                    {!memberHasCredit && <div style={styles.comingSoon}>No credit balance</div>}
                  </div>
                );
              })()}
              <div style={styles.payMethodWrap}>
                <button
                  style={{ ...styles.payMethodBtn, ...styles.payMethodBtnDisabled }}
                  disabled
                  title="Coming Soon">
                  🎁 Gift Card
                </button>
                <div style={styles.comingSoon}>Coming Soon</div>
              </div>
            </div>

            {paymentMethod === 'member_credit' && selectedMember && parseFloat(selectedMember.credit_balance) > 0 && (() => {
              const total = getTotal();
              const balance = parseFloat(selectedMember.credit_balance);
              const maxApply = Math.min(balance, total);
              const remaining = Math.max(0, total - creditNum());
              return (
                <div style={styles.creditPanel}>
                  <p style={styles.label}>Apply credit to this payment</p>
                  <div style={styles.creditApplyRow}>
                    <button style={styles.applyAllBtn} onClick={() => setCreditApplied(maxApply.toFixed(2))}>
                      Apply All (${maxApply.toFixed(2)})
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      min="0"
                      max={maxApply}
                      step="0.01"
                      value={creditApplied}
                      onFocus={(e) => e.target.select()}
                      onChange={e => {
                        const raw = e.target.value;
                        if (raw === '' || raw === '.') { setCreditApplied(raw); return; }
                        if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
                        const v = parseFloat(raw);
                        if (isNaN(v) || v < 0) { setCreditApplied(''); return; }
                        setCreditApplied(v > maxApply ? maxApply.toFixed(2) : raw);
                      }}
                      style={styles.creditInput}
                      placeholder="0.00"
                    />
                  </div>
                  <div style={styles.remainingRow}>
                    Remaining balance: <span style={styles.remainingAmount}>${remaining.toFixed(2)}</span>
                  </div>
                  {remaining > 0 && (
                    <div style={styles.secondaryPay}>
                      <p style={styles.label}>Pay remaining ${remaining.toFixed(2)} with:</p>
                      <div style={styles.tipRow}>
                        <button
                          style={{ ...styles.tipBtn, background: secondaryMethod === 'card' ? '#C9A84C' : '#2A2A2A', color: secondaryMethod === 'card' ? '#000' : '#CCC' }}
                          onClick={() => setSecondaryMethod('card')}>
                          💳 Card
                        </button>
                        <button
                          style={{ ...styles.tipBtn, background: secondaryMethod === 'cash' ? '#C9A84C' : '#2A2A2A', color: secondaryMethod === 'cash' ? '#000' : '#CCC' }}
                          onClick={() => setSecondaryMethod('cash')}>
                          💵 Cash
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={styles.modalBtns}>
              <button
                style={styles.cancelBtn}
                onClick={() => { setShowPayment(false); setPaymentMethod(null); setCreditApplied(''); setSecondaryMethod(null); }}>
                Back
              </button>
              {(() => {
                const total = getTotal();
                const disabled = loading
                  || !paymentMethod
                  || (paymentMethod === 'member_credit' && creditNum() <= 0)
                  || (paymentMethod === 'member_credit' && creditNum() < total && !secondaryMethod);
                return (
                  <button
                    style={{ ...styles.confirmBtn, ...(disabled ? styles.confirmBtnDisabled : {}) }}
                    onClick={handleCloseTab}
                    disabled={disabled}>
                    {loading ? 'Processing...' : 'Confirm Payment'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirmation && lastPayment && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalCard, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h3 style={{ ...styles.modalTitle, textAlign: 'center' }}>Payment Complete</h3>
            <div style={{ color: '#C9A84C', fontSize: 36, fontFamily: 'Georgia', margin: '16px 0' }}>${lastPayment.total.toFixed(2)}</div>
            <div style={{ color: '#888', fontSize: 13, fontFamily: 'Georgia', marginBottom: 16 }}>
              <div style={{ marginBottom: 6 }}>{lastPayment.tabName}</div>
              {lastPayment.member && <div style={{ color: '#C9A84C' }}>{lastPayment.member}</div>}
            </div>
            <div style={{ background: '#2A2A2A', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={styles.confirmRow}><span>Subtotal</span><span>${lastPayment.subtotal.toFixed(2)}</span></div>
              <div style={styles.confirmRow}><span>Tax (6%)</span><span>${lastPayment.tax.toFixed(2)}</span></div>
              {/* Tip hidden */}
              <div style={{ ...styles.confirmRow, color: '#FFF', borderTop: '1px solid #333', paddingTop: 8, marginTop: 4 }}>
                <span>TOTAL</span><span style={{ color: '#C9A84C' }}>${lastPayment.total.toFixed(2)}</span>
              </div>
            </div>
            {lastPayment.creditUsed > 0 && (
              <div style={{ background: '#0D2010', border: '1px solid #2A4A2A', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ color: '#5DBB63', fontSize: 13, fontFamily: 'Georgia' }}>✓ Credit Applied: ${lastPayment.creditUsed.toFixed(2)}</div>
                {lastPayment.charged > 0 && <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>Charged to card: ${lastPayment.charged.toFixed(2)}</div>}
                {lastPayment.charged === 0 && <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>Fully covered by credit</div>}
                <div style={{ color: '#5DBB63', fontSize: 12, marginTop: 4 }}>Remaining credit: ${lastPayment.remainingCredit.toFixed(2)}</div>
              </div>
            )}
            <div style={{ color: '#666', fontSize: 12, fontFamily: 'Georgia', marginBottom: 20, letterSpacing: 2, textTransform: 'uppercase' }}>Paid by {lastPayment.method}</div>
            <button style={{ ...styles.confirmBtn, width: '100%', padding: '14px 0' }} onClick={() => { setShowConfirmation(false); setLastPayment(null); }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { height: '100dvh', background: '#0D0D0D', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  notification: { position: 'fixed', top: 20, right: 20, padding: '12px 24px', borderRadius: 8, color: '#FFF', fontFamily: 'Georgia', fontSize: 14, zIndex: 1000, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },

  // Desktop header
  // Header bar + button styles intentionally mirror GlobalNav.jsx so navigating between
  // POS and other pages produces zero visual shift. POS keeps two documented extras that
  // GlobalNav does not render on desktop: (1) the staff label cluster on the left
  // (headerLeft + headerStaff), and (2) the trailing "Sign Out" button. If you change
  // any of: bar height/padding, headerRight gap, or headerBtn padding/font/border,
  // update GlobalNav.jsx in the same commit to keep them in sync.
  header: { background: '#1A1A1A', borderBottom: '1px solid #2A2A2A', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 80, flexShrink: 0, gap: 12 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 24, flexShrink: 0 },
  headerStaff: { color: '#666', fontSize: 13 },
  headerRight: { display: 'flex', gap: 4, flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto', minWidth: 0 },
  headerBtn: { background: '#2A2A2A', border: '1px solid #3A3A3A', borderRadius: 8, color: '#CCC', padding: '6px 8px', cursor: 'pointer', fontSize: 10.5, fontFamily: 'Georgia', minHeight: 44, whiteSpace: 'nowrap', transition: 'all 0.15s', flexShrink: 0 },

  // Mobile header
  headerMobile: { background: '#1A1A1A', borderBottom: '1px solid #2A2A2A', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56, flexShrink: 0 },
  headerBtnSm: { background: '#2A2A2A', border: '1px solid #333', borderRadius: 6, color: '#CCC', padding: '6px 12px', cursor: 'pointer', fontSize: 12, minHeight: 36, touchAction: 'manipulation' },

  main: { display: 'flex', flex: 1, overflow: 'hidden' },
  leftPanel: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' },
  categories: { display: 'flex', gap: 8, padding: '10px 12px', borderBottom: '1px solid #2A2A2A', overflowX: 'auto', flexShrink: 0, WebkitOverflowScrolling: 'touch' },
  categoryBtn: { padding: '8px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'Georgia', whiteSpace: 'nowrap', minHeight: 44, touchAction: 'manipulation' },
  itemsGrid: { display: 'grid', gap: 10, padding: 12, overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch', alignContent: 'start' },
  itemBtn: { background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', textAlign: 'left' },
  itemName: { color: '#DDD', fontSize: 13, fontFamily: 'Georgia', lineHeight: 1.3 },
  itemPrice: { color: '#C9A84C', fontSize: 16, fontWeight: 'bold', fontFamily: 'Georgia' },
  emptyItems: { gridColumn: '1/-1', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 },

  // Right panel (desktop)
  rightPanel: { width: 380, display: 'flex', flexDirection: 'column', background: '#111', borderLeft: '1px solid #2A2A2A', flexShrink: 0 },
  tabsRow: { display: 'flex', gap: 8, padding: '10px 12px', borderBottom: '1px solid #2A2A2A', overflowX: 'auto', flexShrink: 0, WebkitOverflowScrolling: 'touch' },
  newTabBtn: { background: '#C9A84C', border: 'none', borderRadius: 8, color: '#000', padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'Georgia', whiteSpace: 'nowrap', fontWeight: 'bold', minHeight: 44, touchAction: 'manipulation' },
  tabsList: { display: 'flex', gap: 8, overflowX: 'auto' },
  tabChip: { padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontFamily: 'Georgia', whiteSpace: 'nowrap', minHeight: 44, touchAction: 'manipulation' },
  tabHeader: { padding: '12px 16px', borderBottom: '1px solid #2A2A2A', flexShrink: 0 },
  tabName: { color: '#C9A84C', fontSize: 15, fontFamily: 'Georgia', fontWeight: 'bold' },
  memberBadge: { display: 'block', color: '#888', fontSize: 12, marginTop: 4 },
  tabItems: { overflowY: 'auto', padding: 14, WebkitOverflowScrolling: 'touch' },
  itemsList: { display: 'flex', flexDirection: 'column', gap: 8 },
  tabItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#1A1A1A', borderRadius: 8, border: '1px solid #2A2A2A' },
  tabItemName: { flex: 1, color: '#DDD', fontSize: 13, fontFamily: 'Georgia' },
  tabItemQty: { color: '#888', fontSize: 12, padding: '2px 8px', background: '#2A2A2A', borderRadius: 4 },
  tabItemPrice: { color: '#C9A84C', fontSize: 14, fontFamily: 'Georgia', minWidth: 60, textAlign: 'right' },
  emptyTab: { color: '#333', fontSize: 13, textAlign: 'center', marginTop: 40 },
  noTab: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: 14, fontFamily: 'Georgia', padding: 40 },
  totals: { padding: 14, paddingBottom: 80, borderTop: '1px solid #2A2A2A', flexShrink: 0 },
  creditRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: 'Georgia', marginBottom: 6, padding: '6px 8px', background: '#0D2010', borderRadius: 6 },
  totalRow: { display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: 13, fontFamily: 'Georgia', marginBottom: 6 },
  totalFinal: { color: '#FFF', fontSize: 16, fontWeight: 'bold', borderTop: '1px solid #2A2A2A', paddingTop: 8, marginTop: 4 },
  chargeBtn: { width: '100%', background: '#C9A84C', border: 'none', borderRadius: 10, color: '#000', fontSize: 16, fontFamily: 'Georgia', fontWeight: 'bold', padding: '16px 0', cursor: 'pointer', marginTop: 12, minHeight: 56, touchAction: 'manipulation' },

  // Modals
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 16 },
  modalCard: { background: '#1A1A1A', borderRadius: 16, padding: 28, width: '100%', maxWidth: 380, border: '1px solid #2A2A2A', maxHeight: '90dvh', overflowY: 'auto' },
  modalTitle: { color: '#C9A84C', fontSize: 18, fontFamily: 'Georgia', marginBottom: 20 },
  input: { width: '100%', background: '#2A2A2A', border: '1px solid #333', borderRadius: 8, color: '#FFF', padding: '12px 16px', fontSize: 16, fontFamily: 'Georgia', marginBottom: 12, outline: 'none', boxSizing: 'border-box' },
  memberResult: { width: '100%', background: '#2A2A2A', border: '1px solid #333', borderRadius: 8, color: '#CCC', padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontFamily: 'Georgia', marginBottom: 6, textAlign: 'left', display: 'flex', justifyContent: 'space-between', minHeight: 48 },
  creditBadge: { color: '#C9A84C', fontSize: 12 },
  selectedMember: { color: '#27AE60', fontSize: 13, fontFamily: 'Georgia', padding: '8px 12px', background: '#0D2010', borderRadius: 8, marginBottom: 12 },
  modalBtns: { display: 'flex', gap: 12, marginTop: 20 },
  cancelBtn: { flex: 1, background: '#2A2A2A', border: 'none', borderRadius: 8, color: '#CCC', padding: '14px 0', cursor: 'pointer', fontFamily: 'Georgia', fontSize: 14, minHeight: 52 },
  confirmBtn: { flex: 1, background: '#C9A84C', border: 'none', borderRadius: 8, color: '#000', padding: '14px 0', cursor: 'pointer', fontFamily: 'Georgia', fontSize: 14, fontWeight: 'bold', minHeight: 52 },
  totalBig: { color: '#C9A84C', fontSize: 40, fontFamily: 'Georgia', textAlign: 'center', margin: '16px 0' },
  label: { color: '#666', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 },
  tipRow: { display: 'flex', gap: 8, marginBottom: 16 },
  tipBtn: { flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'Georgia', minHeight: 48 },
  confirmRow: { display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: 13, fontFamily: 'Georgia', marginBottom: 6 },
  creditAvailable: { background: '#1A1408', border: '1px solid #3A2E10', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#C9A84C', fontSize: 13, fontFamily: 'Georgia' },
  payMethodGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 },
  payMethodBtn: { padding: '14px 0', borderRadius: 8, border: '1px solid #2A2A2A', cursor: 'pointer', fontSize: 13, fontFamily: 'Georgia', minHeight: 52, background: '#2A2A2A', color: '#CCC' },
  payMethodBtnActive: { background: '#C9A84C', color: '#000', borderColor: '#C9A84C' },
  payMethodBtnDisabled: { background: '#1A1A1A', color: '#555', cursor: 'not-allowed', borderColor: '#222' },
  payMethodWrap: { display: 'flex', flexDirection: 'column', gap: 4 },
  comingSoon: { color: '#666', fontSize: 10, fontFamily: 'Georgia', textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' },
  creditPanel: { background: '#0D0D0D', border: '1px solid #2A2A2A', borderRadius: 10, padding: 14, marginBottom: 12 },
  creditApplyRow: { display: 'flex', gap: 8, marginBottom: 10 },
  applyAllBtn: { flex: 1, background: '#2A2A2A', border: '1px solid #C9A84C', borderRadius: 8, color: '#C9A84C', padding: '10px 12px', cursor: 'pointer', fontSize: 13, fontFamily: 'Georgia', minHeight: 44 },
  creditInput: { flex: 1, background: '#1A1A1A', border: '1px solid #333', borderRadius: 8, color: '#FFF', padding: '10px 12px', fontSize: 14, fontFamily: 'Georgia', outline: 'none', boxSizing: 'border-box', minHeight: 44 },
  remainingRow: { color: '#888', fontSize: 13, fontFamily: 'Georgia', marginBottom: 8 },
  remainingAmount: { color: '#C9A84C', fontWeight: 'bold' },
  secondaryPay: { borderTop: '1px solid #2A2A2A', paddingTop: 12, marginTop: 4 },
  confirmBtnDisabled: { background: '#3A3A3A', color: '#777', cursor: 'not-allowed' },
};

export default POS;
